import { useMemo } from "react";
import { BookingService } from "@/services/BookingService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { encryptForMember } from "@/lib/encryption";
import { useMember } from "@/contexts/memberContext";
import type { Booking } from "@/types/Booking";

interface AddBookingNotePayload {
  bookingId: string;
  notes: string;
}

/**
 * Hook for adding encrypted notes to a booking
 * Notes are encrypted client-side using the member's encryption key before being sent to the server
 */
export function useAddBookingNote() {
  const queryClient = useQueryClient();
  const bookingService = useMemo(() => new BookingService(), []);
  const { member } = useMember();

  return useMutation<Booking | null, Error, AddBookingNotePayload>({
    mutationFn: async ({ bookingId, notes }: AddBookingNotePayload) => {
      if (!member) {
        throw new Error("Member not authenticated");
      }

      // Get encryption key from member's userKey field
      let encryptionKey = member.userKey;
      if (!encryptionKey) {
        throw new Error("Member encryption key not available. Please contact support.");
      }

      // Encrypt the notes client-side
      const encryptedNotes = await encryptForMember(notes, encryptionKey);

      // Send encrypted notes to server
      return bookingService.addNotesToBooking(bookingId, encryptedNotes);
    },
    onSuccess: (updatedBooking) => {
      if (updatedBooking) {
        // Invalidate and refetch booking queries
        queryClient.invalidateQueries({ queryKey: ["booking", updatedBooking.id] });
        queryClient.invalidateQueries({ queryKey: ["bookings"] });
      }
    },
  });
}
